import React from 'react';

import { Box, Popover, PopoverBody, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { useGetFunctionTreeQuery } from 'components/RabbitOKRCreationPanel/datahooks/useGetFunctionTreeQuery';

import { DepartmentName } from 'assets/assetsV1/iconComponents/DepartmentName';

import { GenericControl, ReadOnlyGenericControl } from 'uiToolkitV1/SelectV2/GenericControl';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const useStyles = makeStyles({
  root: {
    maxHeight: 350,
    flexGrow: 1,
    maxWidth: 450,
    minWidth: 200,
    padding: 8,
  },
  treeItem: {
    paddingTop: '2px',
    paddingBottom: '2px',
    color: '#333333',
  },
  label: {
    fontSize: '14px !important',
    padding: '4px',
    paddingLeft: '8px',
    borderRadius: '4px',
  },
});

/* 
  relying on following helpers to make and get id, name from nodeId. 
  TreeView's treeitem only expects a unique Id and return that only on selection of a treeitem
*/
const separator = '+++++++++';
const getNodeIdFromIdAndName = (id, name) => id + separator + name;
const getIdAndNameFromNodeId = (nodeId) => {
  const [id, name] = nodeId.split(separator);
  return { id, name };
};

const FunctionTree = ({ departmentName, departmentId, onChange }) => {
  const classes = useStyles();
  const { data, isError, isLoading } = useGetFunctionTreeQuery();

  const renderTree = (node) => {
    const { childFunctions, function: department } = node;
    return (
      <TreeItem
        classes={{ root: classes.treeItem, label: classes.label }}
        key={department?.uuid}
        nodeId={getNodeIdFromIdAndName(department?.uuid, department?.name)}
        label={department?.name}
      >
        {Array.isArray(childFunctions) ? childFunctions.map((node) => renderTree(node)) : null}
      </TreeItem>
    );
  };

  // only select if icon wasn't clicked
  const handleSelect = (e, nodeId) => {
    e.persist();
    const iconClicked = e.target.closest('.MuiTreeItem-iconContainer');
    if (!iconClicked) {
      const { id, name } = getIdAndNameFromNodeId(nodeId);

      onChange({ departmentId: id, departmentName: name });
    }
  };

  if (isLoading) {
    return '...loading';
  }

  if (isError) {
    return 'Error';
  }

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      onNodeSelect={handleSelect}
    >
      {Array.isArray(data) ? data.map((node) => renderTree(node)) : null}
    </TreeView>
  );
};

export const DepartmentSelectionDropdown = ({
  departmentName = null,
  departmentId = null,
  onChange,
  isReadOnly = false,
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();

  const handleSelection = (changes) => {
    onClose();
    onChange(changes);
  };

  const ControlComponentToUse = isReadOnly ? ReadOnlyGenericControl : GenericControl;
  const placeholder = isReadOnly ? '-' : 'Select';

  return (
    <Box>
      <Popover placement="bottom-start" flip returnFocusOnClose={false} isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>
          <ControlComponentToUse onClick={onToggle}>
            <DepartmentName boxSize="24px" color="text.g2" fill="none" marginRight="8px" />
            <TextBodyMedium color={departmentId ? 'text.g1' : 'text.g2'}>
              {departmentId ? departmentName : placeholder}
            </TextBodyMedium>
          </ControlComponentToUse>
        </PopoverTrigger>
        <PopoverContent maxW="280px">
          <PopoverBody px={2} maxH="240px" overflowY="auto">
            <FunctionTree departmentId={departmentId} departmentName={departmentName} onChange={handleSelection} />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
